<template>
	<highcharts class="highcharts-container mb-100" :options="chartOptions"></highcharts>
</template>

<script setup lang="ts">
import BaseChart from './BaseChartOptions';
import { computed } from 'vue';
import deepMerge from 'deepmerge';
import { Chart as highcharts } from 'highcharts-vue';
import { Options } from 'highcharts';

const chartOptions = computed((): Options => {
	const assetNamesData = [
		{
			name: 'Inflation',
			x: 1,
			y: 2.5
		},
		{
			name: 'Treasury bills',
			x: 2.5,
			y: 4
		},
		{
			name: 'Investment grade bonds',
			x: 6,
			y: 8,
			dataLabels: { align: 'center', y: -8 }
		},
		{
			name: 'Buyout funds',
			x: 7.5,
			y: 17
		},
		{
			name: 'Treasury bonds',
			x: 10,
			y: 8
		},
		{
			name: 'Commodities',
			x: 13.5,
			y: -1
		},
		{
			name: 'Large cap equities',
			x: 17,
			y: 12
		},
		{
			name: 'Mid cap equities',
			x: 18,
			y: 13
		},
		{
			name: 'REITs',
			x: 18.2,
			y: 10.7,
			dataLabels: { align: 'right', x: -5, y: 12 }
		},
		{
			name: 'Venture capital',
			x: 21,
			y: 21,
			color: '#e26442'
		}
	];

	const mobileSpecificAssetNames = [
		{
			name: 'Small cap<br/>equities',
			x: 19,
			y: 10.5,
			dataLabels: { align: 'center', x: 0, y: 35 }
		},
		{
			name: 'Non-U.S.<br/>equities',
			x: 22.3,
			y: 10.2,
			dataLabels: { align: 'center', x: 10, y: 35 }
		}
	];

	const mediumSpecificAssetNames = [
		{
			name: 'Small cap<br/>equities',
			x: 19,
			y: 10.5,
			dataLabels: { align: 'center', y: 40 }
		},
		{
			name: 'Non-U.S.<br/>equities',
			x: 22.3,
			y: 10.2,
			dataLabels: { align: 'center', x: 10, y: 40 }
		}
	];

	const desktopSpecificAssetNames = [
		{
			name: 'Small cap<br/>equities',
			x: 19,
			y: 10.5,
			dataLabels: { align: 'left', y: 30 }
		},
		{ name: 'Non-U.S.<br/>equities', x: 22.3, y: 10.2, dataLabels: { y: 30 } }
	];

	const axisStyle = {
		fontSize: '14px',
		fontWeight: '600',
		color: '#54534E'
	};

	const options: Options = {
		chart: {
			type: 'scatter',
			backgroundColor: '#ffffff',
			style: {
				color: '#151513'
			},
			spacing: [20, 10, 20, 5],
			marginLeft: 50,
			marginBottom: 75,
			height: 508
		},
		title: {
			text: 'Risk and reward for asset classes, 1984&#8209;2015',
			style: {
				fontFamily: '"Ivar Headline", "Source Sans Pro", "Helvetica Neue", "Helvetica", "Arial", sans-serif',
				fontSize: '16px',
				fontWeight: 'bold'
			}
		},
		xAxis: [
			{
				offset: -65,
				lineColor: '#000000',
				tickInterval: 0,
				showFirstLabel: false,
				showLastLabel: false,
				tickColor: 'rgba(0,0,0,0)'
			},
			{
				title: {
					text: 'Standard Deviation (Percent)',
					style: axisStyle,
					y: 10
				},
				tickInterval: 5,
				min: 0,
				endOnTick: true,
				tickLength: 0,
				offset: 0,
				lineWidth: 0,
				minorGridLineWidth: 0,
				minorTickLength: 0
			}
		],
		yAxis: {
			gridLineWidth: 0,
			lineWidth: 1,
			lineColor: '#000000',
			height: 372,
			title: {
				text: 'Annual Return (Percent)',
				style: axisStyle,
				x: 5
			}
		},
		plotOptions: {
			scatter: {
				dataLabels: {
					enabled: true
				}
			},
			series: {
				enableMouseTracking: false
			}
		},
		series: [
			{
				id: 'revenueLine',
				name: 'revenue line',
				type: 'line',
				dashStyle: 'ShortDash',
				color: 'rgba(121, 125, 127, .5)',
				marker: {
					enabled: false
				},
				data: [
					[1, 5],
					[22, 14]
				],
				states: {
					hover: {
						enabled: false
					}
				},
				xAxis: 1
			},
			{
				id: 'assetNames',
				name: 'asset names',
				color: '#3374ef',
				type: 'scatter',
				dataLabels: {
					format: '{point.name}',
					allowOverlap: true,
					style: {
						fontFamily:
							'"Ivar Headline", "Source Sans Pro", "Helvetica Neue", "Helvetica", "Arial", sans-serif',
						fontSize: '10px'
					},
					align: 'left',
					y: 10
				},
				xAxis: 1,
				data: [...assetNamesData, ...mobileSpecificAssetNames]
			}
		],
		responsive: {
			rules: [
				{
					condition: {
						minWidth: 961
					},
					chartOptions: {
						chart: {
							spacing: [20, 30, 20, 20],
							marginLeft: 75
						},
						title: {
							style: {
								fontSize: '18px'
							}
						},
						yAxis: {
							title: {
								x: -10
							}
						},
						series: [
							{ type: 'line', id: 'revenueLine' },
							{
								id: 'assetNames',
								type: 'scatter',
								dataLabels: { style: { fontSize: '14px' }, y: 12 },
								data: [...assetNamesData, ...desktopSpecificAssetNames]
							}
						]
					}
				},
				{
					condition: {
						minWidth: 481,
						maxWidth: 960
					},
					chartOptions: {
						chart: {
							spacing: [20, 20, 20, 10],
							marginLeft: 65
						},
						title: {
							style: {
								fontSize: '18px'
							}
						},
						yAxis: {
							title: {
								x: -10
							}
						},
						series: [
							{ type: 'line', id: 'revenueLine' },
							{
								id: 'assetNames',
								type: 'scatter',
								dataLabels: { style: { fontSize: '12px' }, y: 12 },
								data: [...assetNamesData, ...mediumSpecificAssetNames]
							}
						]
					}
				}
			]
		}
	};

	return deepMerge(new BaseChart().getOptions(), options, { arrayMerge: (dest, source) => source });
});
</script>
